import { useId, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "@nexus/ui";
import type { DashboardCreativesRequest } from "@/lib/queries/dashboard.queries";
import type { DrilldownSearch } from "@/features/drilldown/lib/drilldown-search";
import type { CompetitorCreativeCardModel } from "@/features/drilldown/lib/drilldown-card.types";
import { mapCompetitorCardRow } from "@/features/drilldown/lib/drilldown-competitor-card-mapper";
import { useCreativeUrlSettings } from "@/lib/queries/system-settings.queries";
import { DrilldownCompetitorCreativeCard } from "@/features/drilldown/components/DrilldownCompetitorCreativeCard";
import { DrilldownCreativeDrawer } from "@/features/drilldown/components/drawer/DrilldownCreativeDrawer";
import type { DashboardKpiCardsState } from "../../lib/card-state";
import { CREATIVE_ROW_SIZE } from "../../lib/competitor-creative-rows";

interface CreativeRowProps {
  title: string;
  subtitle: string;
  /** `blocked`: the applied filters lack a brand or competitor, so nothing can load yet. */
  state: DashboardKpiCardsState | "blocked";
  rows: ReadonlyArray<Record<string, unknown>>;
  /** The window the row's creatives were fetched over; the drawer's stats use it too. */
  creativeWindow: Pick<DashboardCreativesRequest, "startDate" | "endDate" | "dateRange">;
  /** Where "Explore in Drilldown" goes: same filters, same sort. */
  drilldownSearch: DrilldownSearch;
  /** Runs just before "Explore in Drilldown" opens Drilldown. */
  onOpenDrilldown?: () => void;
  /** Page reset key (tenant, mode, filters); a change closes the open drawer. */
  resetKey: string;
}

/** One line of cards that share the width, down to 184px each, then scroll sideways. */
const ROW_TRACK_CLASS = "grid grid-flow-col auto-cols-[minmax(184px,1fr)] gap-4 overflow-x-auto pb-2";

export function CreativeRow({
  title,
  subtitle,
  state,
  rows,
  creativeWindow,
  drilldownSearch,
  onOpenDrilldown,
  resetKey,
}: CreativeRowProps) {
  const { t } = useTranslation();
  const { competitorsPath } = useCreativeUrlSettings();
  const headingId = useId();
  const [openCreative, setOpenCreative] = useState<CompetitorCreativeCardModel | null>(null);
  // Cleared during render, not in an effect, so the drawer never renders the old creative over the new window.
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setOpenCreative(null);
  }
  const cards = useMemo(
    () =>
      rows
        .slice(0, CREATIVE_ROW_SIZE)
        .map((row, index) => mapCompetitorCardRow(row, index, competitorsPath)),
    [rows, competitorsPath],
  );

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) setOpenCreative(null);
  };

  return (
    <section className="flex min-w-0 flex-col gap-4" aria-labelledby={headingId}>
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-[22px] font-semibold leading-[1.3] text-foreground">
            {title}
          </h2>
          <p className="text-[13px] text-muted-foreground">{subtitle}</p>
        </div>
        <Button asChild variant="secondary" size="sm" className="shrink-0">
          <Link to="/drilldown" search={drilldownSearch} onClick={onOpenDrilldown}>
            {t("dashboard.creativeRows.explore", "Explore in Drilldown")}
            <Icon name="arrow-up-right" size="sm" color="inherit" />
          </Link>
        </Button>
      </div>

      {state === "loading" && (
        <div className={ROW_TRACK_CLASS} aria-busy>
          {Array.from({ length: CREATIVE_ROW_SIZE }, (_, i) => (
            <div key={i} className="skeleton-shimmer-bar h-[330px] rounded-md" />
          ))}
        </div>
      )}

      {state === "blocked" && (
        <p className="text-sm text-muted-foreground">
          {t(
            "dashboard.creativeRows.selectionRequired",
            "Select your brand and main competitor to see creatives.",
          )}
        </p>
      )}

      {state === "error" && (
        <p className="text-sm text-destructive">
          {t("dashboard.creativeRows.error", "Failed to load creatives. Please try again.")}
        </p>
      )}

      {(state === "empty" || (state === "ready" && cards.length === 0)) && (
        <p className="text-sm text-muted-foreground">
          {t("dashboard.creativeRows.empty", "No creatives match these filters.")}
        </p>
      )}

      {state === "ready" && cards.length > 0 && (
        <div className={ROW_TRACK_CLASS}>
          {cards.map((card) => (
            <DrilldownCompetitorCreativeCard
              key={card.id}
              creative={card}
              onCardClick={() => setOpenCreative(card)}
            />
          ))}
        </div>
      )}

      <DrilldownCreativeDrawer
        scope="dashboard"
        creative={openCreative}
        open={openCreative !== null}
        onOpenChange={handleDrawerOpenChange}
        startDate={creativeWindow.startDate}
        endDate={creativeWindow.endDate}
        dateRange={creativeWindow.dateRange}
      />
    </section>
  );
}
